import React from 'react';
import { Box, Typography, Paper, Grid, Divider, Alert, Chip } from '@mui/material';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';

const ForecastReview = ({ formData, onValidationChange }) => {
  
  // Load platform settings for dynamic pricing
  const { settings: platformSettings } = usePlatformSettings();
  const currentPrice = platformSettings.betaMode 
    ? platformSettings.betaPrice 
    : platformSettings.reportPrice;
  
  // Validate on mount - always valid since this is just review
  React.useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  const formatSliderValue = (value, unit) => {
    return `${value} ${unit}${value !== 1 ? 's' : ''}`;
  };

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ 
        fontWeight: 300, 
        mb: 3,
        fontFamily: 'monospace',
        color: '#fafafa'
      }}>
        Step 3: Review & Payment
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, color: '#737373' }}>
        Review your information before proceeding. Your report will be ready in about 60 seconds after payment.
      </Typography>

      {/* Structured Data Summary */}
      <Paper sx={{ 
        mb: 3, 
        p: 3, 
        backgroundColor: '#171717', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Typography variant="h6" gutterBottom sx={{ 
          color: '#fafafa', 
          fontFamily: 'monospace',
          fontSize: '1.125rem',
          mb: 2
        }}>
          Your Data Profile
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Age
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              {formData.age || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Income Trajectory
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              {formData.incomeTrajectory || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Relationship Status
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              {formData.relationshipStatus || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Biggest Expense
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              {formData.biggestExpense || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Weekly Exercise
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              {formatSliderValue(formData.weeklyExerciseHours || 0, 'hour')}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Sleep Debt
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              {formatSliderValue(formData.sleepDebtHours || 0, 'hour')}/week
            </Typography>
          </Grid>

          {formData.photoDescription && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace', mb: 1 }}>
                Workspace Description
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#fafafa', 
                fontFamily: 'monospace',
                backgroundColor: '#0a0a0a',
                p: 2,
                borderRadius: '4px',
                fontStyle: 'italic'
              }}>
                "{formData.photoDescription}"
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Chat Log Summary */}
      <Paper sx={{ 
        mb: 3, 
        p: 3, 
        backgroundColor: '#171717', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Typography variant="h6" gutterBottom sx={{ 
          color: '#fafafa', 
          fontFamily: 'monospace',
          fontSize: '1.125rem',
          mb: 2
        }}>
          Chat Log Analysis
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Word Count
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              {formData.chatLogWordCount || 0} words
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Estimated Tokens
            </Typography>
            <Typography variant="body1" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 600 }}>
              ~{formData.chatLogTokenCount || 0} tokens
            </Typography>
          </Grid>
        </Grid>

        {formData.chatLogCompressionRatio && (
          <Box sx={{ mt: 2 }}>
            <Chip 
              label={`Optimized: ${formData.chatLogCompressionRatio}% reduction`}
              sx={{ 
                backgroundColor: '#0a0a0a', 
                color: '#10b981', 
                fontFamily: 'monospace',
                border: '1px solid #10b981'
              }} 
            />
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 2, backgroundColor: '#0a0a0a', border: '1px solid #262626', color: '#fafafa' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            Your chat log will be processed and then <strong>permanently deleted</strong>. We never store your conversations.
          </Typography>
        </Alert>
      </Paper>

      <Divider sx={{ my: 3, borderColor: '#262626' }} />

      {/* What Happens Next */}
      <Paper sx={{ 
        mb: 3, 
        p: 3, 
        backgroundColor: '#171717', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Typography variant="h6" gutterBottom sx={{ 
          color: '#fafafa', 
          fontFamily: 'monospace',
          fontSize: '1.125rem',
          mb: 2
        }}>
          What Happens Next
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              backgroundColor: '#0a0a0a', 
              border: '1px solid #262626',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontFamily: 'monospace',
              color: '#fafafa',
              fontSize: '0.875rem',
              flexShrink: 0
            }}>
              1
            </Box>
            <Box>
              <Typography variant="body1" sx={{ color: '#fafafa', fontWeight: 500, mb: 0.5 }}>
                Payment Processing
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373' }}>
                Secure payment via Stripe (${currentPrice} one-time)
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              backgroundColor: '#0a0a0a', 
              border: '1px solid #262626',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontFamily: 'monospace',
              color: '#fafafa',
              fontSize: '0.875rem',
              flexShrink: 0
            }}>
              2
            </Box>
            <Box>
              <Typography variant="body1" sx={{ color: '#fafafa', fontWeight: 500, mb: 0.5 }}>
                Deep Pattern Recognition
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373' }}>
                Identify your cognitive patterns, emotional tendencies, and thinking style
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              backgroundColor: '#0a0a0a', 
              border: '1px solid #262626',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontFamily: 'monospace',
              color: '#fafafa',
              fontSize: '0.875rem',
              flexShrink: 0
            }}>
              3
            </Box>
            <Box>
              <Typography variant="body1" sx={{ color: '#fafafa', fontWeight: 500, mb: 0.5 }}>
                Forecast Generation (Stage 2)
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373' }}>
                Generate your 5-year trajectory, blind lever, and 30-day deflection
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              backgroundColor: '#0a0a0a', 
              border: '1px solid #262626',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontFamily: 'monospace',
              color: '#fafafa',
              fontSize: '0.875rem',
              flexShrink: 0
            }}>
              4
            </Box>
            <Box>
              <Typography variant="body1" sx={{ color: '#fafafa', fontWeight: 500, mb: 0.5 }}>
                PDF Delivery
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373' }}>
                3-page PDF report delivered to your dashboard (&lt;3 minutes total)
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Pricing & Disclaimer */}
      <Paper sx={{ 
        p: 3, 
        backgroundColor: '#0a0a0a', 
        border: '1px solid #262626',
        borderRadius: '8px'
      }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h3" sx={{ 
            color: '#fafafa', 
            fontFamily: 'monospace',
            fontWeight: 300,
            mb: 1
          }}>
            ${currentPrice}
          </Typography>
          <Typography variant="body2" sx={{ color: '#737373' }}>
            One-time payment. No subscription.
          </Typography>
        </Box>

        <Divider sx={{ my: 2, borderColor: '#262626' }} />

        <Typography variant="body2" sx={{ color: '#525252', fontStyle: 'italic', textAlign: 'center' }}>
          For reflection and entertainment. Not medical or financial advice.
        </Typography>
      </Paper>

      {/* Click "Complete Purchase" to proceed */}
      <Alert severity="info" sx={{ mt: 3, backgroundColor: '#171717', border: '1px solid #262626', color: '#fafafa' }}>
        <Typography variant="body2">
          Click <strong>"Complete Purchase"</strong> below to proceed to secure payment.
        </Typography>
      </Alert>
    </Box>
  );
};

export default ForecastReview;

